import { useCallback, useEffect, useRef, useState } from "react";

/**
 * The rendered height of a page's own sticky header, so whatever sticks BELOW it can come to rest
 * against it instead of sliding underneath.
 *
 * <p>This exists because `top: 0` on a second sticky element is almost always a bug: it pins that
 * element to the top of the SCROLL CONTAINER, not to the bottom of the chrome above it, so the two
 * occupy the same band and the one with the lower z-index is sliced away. That is exactly what
 * happened to the builder's targets bar — its numbers row disappeared under the menu header,
 * leaving the progress bars showing beneath it.
 *
 * <p>Measured rather than hard-coded because the header WRAPS: two rows on a wide window become
 * three or four on a narrow one, and a constant that was right at 1440px clips the card at 1100.
 *
 * <p>Returns a callback ref rather than a `useRef` object: the node arrives on mount and again
 * whenever the tree re-mounts it, which a `useRef` + `useEffect([])` pair silently misses.
 */
export const useStickyHeaderHeight = (): [(node: HTMLElement | null) => void, number] => {
  const [height, setHeight] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node) return;

    // Border-box height — the header carries a bottom border, and the offset has to clear it.
    const measure = (el: HTMLElement) => setHeight(el.getBoundingClientRect().height);
    measure(node);

    // jsdom has no ResizeObserver. The measurement above is still taken, so a test sees a number
    // (0 in jsdom, which lays nothing out) rather than a crash.
    if (typeof ResizeObserver === "undefined") return;
    observer.current = new ResizeObserver(([entry]) => measure(entry.target as HTMLElement));
    observer.current.observe(node);
  }, []);

  useEffect(() => () => observer.current?.disconnect(), []);

  return [ref, height];
};

export default useStickyHeaderHeight;
